/**
 * Queries React Query du module YouTube Studio (admin).
 *
 * Les formes de lignes sont déclarées explicitement ci-dessous (alignées sur la
 * migration 0107). L'accès passe par `ytDb` (cast léger) pour éviter l'inférence
 * profonde de PostgREST sur les selects avec jointures — même pattern que
 * `pipeline-queries.ts` (`as unknown as ...`).
 */

import { useQuery } from '@tanstack/react-query';
import type {
  YoutubeVideoStatus,
  YoutubeJobStatus,
  YoutubeJobStep,
  YoutubeAssetKind,
  AspectRatio,
  VideoOverlay,
  SocialPlatform,
  SocialPrivacy,
} from '@jeraisonne/db/schemas/youtube';
import type { SubjectSlug, SchoolLevel } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';

/** Accès aux tables youtube_* sans inférence PostgREST profonde (cf. note ci-dessus). */
export const ytDb = supabase as unknown as {
  from: (table: string) => any;
};

// ── Types de lignes ──────────────────────────────────────────────────────────

export interface YoutubeAssetRow {
  id: string;
  kind: YoutubeAssetKind;
  label: string;
  bucket_path: string;
  mime: string;
  bytes: number | null;
  duration_ms: number | null;
  width: number | null;
  height: number | null;
  music_artist: string | null;
  music_license: string | null;
  music_source_url: string | null;
  created_at: string;
}

export interface YoutubeVideoRow {
  id: string;
  created_by: string;
  status: YoutubeVideoStatus;
  exercise_id: string;
  attempt_id: string | null;
  background_asset_id: string | null;
  music_asset_id: string | null;
  aspect_ratio: AspectRatio;
  duration_seconds: number;
  loop_background: boolean;
  overlay: VideoOverlay;
  yt_title: string | null;
  yt_description: string | null;
  yt_tags: string[];
  social_caption: string | null;
  created_at: string;
  updated_at: string;
}

export interface YoutubeJobRow {
  id: string;
  video_id: string;
  status: YoutubeJobStatus;
  current_step: YoutubeJobStep | null;
  worker_id: string | null;
  heartbeat_at: string | null;
  restart_count: number;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export interface SocialPublicationRow {
  id: string;
  video_id: string;
  platform: SocialPlatform;
  privacy: SocialPrivacy;
  scheduled_at: string | null;
  status: YoutubeVideoStatus;
  external_id: string | null;
  external_url: string | null;
  error: string | null;
}

export interface YoutubeVideoWithExercise extends YoutubeVideoRow {
  exercise: { id: string; title: string } | null;
}

export interface YoutubeVideoItemRow {
  id: string;
  video_id: string;
  exercise_id: string;
  attempt_id: string | null;
  position: number;
}

// ── Queries ──────────────────────────────────────────────────────────────────

export function useYoutubeVideos() {
  return useQuery({
    queryKey: ['youtube-videos'],
    queryFn: async (): Promise<YoutubeVideoWithExercise[]> => {
      const { data, error } = await ytDb
        .from('youtube_videos')
        .select('*, exercise:exercises(id, title)')
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as YoutubeVideoWithExercise[];
    },
    refetchInterval: 5000,
  });
}

export function useYoutubeVideo(videoId: string, opts?: { live?: boolean }) {
  return useQuery({
    queryKey: ['youtube-video', videoId],
    enabled: !!videoId,
    refetchInterval: opts?.live ? 5000 : false,
    queryFn: async (): Promise<{
      video: YoutubeVideoWithExercise;
      job: YoutubeJobRow | null;
      publications: SocialPublicationRow[];
    } | null> => {
      const { data, error } = await ytDb
        .from('youtube_videos')
        .select('*, exercise:exercises(id, title)')
        .eq('id', videoId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;

      const { data: jobs } = await ytDb
        .from('youtube_jobs')
        .select('*')
        .eq('video_id', videoId)
        .order('created_at', { ascending: false })
        .limit(1);

      const { data: pubs } = await ytDb
        .from('social_publications')
        .select('*')
        .eq('video_id', videoId);

      return {
        video: data as YoutubeVideoWithExercise,
        job: (jobs?.[0] ?? null) as YoutubeJobRow | null,
        publications: (pubs ?? []) as SocialPublicationRow[],
      };
    },
  });
}

/**
 * Playlist ordonnée d'une vidéo (table `youtube_video_items`) avec le contexte
 * exercice/chapitre nécessaire pour reconstituer la sélection à l'édition.
 */
export type YoutubeVideoItemWithContext = {
  exercise_id: string;
  attempt_id: string | null;
  position: number;
  title: string;
  level: SchoolLevel | null;
  subject: SubjectSlug | null;
  chapter_title: string | null;
};

export function useYoutubeVideoItems(videoId: string, enabled = true) {
  return useQuery({
    queryKey: ['youtube-video-items', videoId],
    enabled: enabled && !!videoId,
    queryFn: async (): Promise<YoutubeVideoItemWithContext[]> => {
      const { data, error } = await ytDb
        .from('youtube_video_items')
        .select(
          'exercise_id, attempt_id, position, exercises ( title, chapters ( title, subject, level ) )',
        )
        .eq('video_id', videoId)
        .order('position', { ascending: true });
      if (error) throw new Error(error.message);
      return ((data ?? []) as any[]).map((r) => ({
        exercise_id: r.exercise_id,
        attempt_id: r.attempt_id,
        position: r.position,
        title: r.exercises?.title ?? '',
        level: (r.exercises?.chapters?.level ?? null) as SchoolLevel | null,
        subject: (r.exercises?.chapters?.subject ?? null) as SubjectSlug | null,
        chapter_title: r.exercises?.chapters?.title ?? null,
      }));
    },
  });
}

export function useYoutubeAssets(kind?: YoutubeAssetKind) {
  return useQuery({
    queryKey: ['youtube-assets', kind ?? 'all'],
    queryFn: async (): Promise<YoutubeAssetRow[]> => {
      let q = ytDb.from('youtube_assets').select('*').order('created_at', { ascending: false });
      if (kind) q = q.eq('kind', kind);
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      return (data ?? []) as YoutubeAssetRow[];
    },
  });
}

export type BackgroundSignedAsset = {
  url: string;
  /** 'image' ou 'video' — détermine le rendu (<img> vs <video>) dans l'aperçu. */
  isVideo: boolean;
};

/**
 * URL signée du fond (image OU vidéo) d'un asset (bucket privé) — pour l'aperçu
 * overlay. Renvoie null si pas d'asset / introuvable / musique.
 */
export function useBackgroundSignedUrl(assetId: string | null) {
  return useQuery({
    queryKey: ['youtube-bg-signed', assetId],
    enabled: !!assetId,
    staleTime: 1000 * 60 * 30,
    queryFn: async (): Promise<BackgroundSignedAsset | null> => {
      const { data: asset } = await ytDb
        .from('youtube_assets')
        .select('bucket_path, kind')
        .eq('id', assetId)
        .maybeSingle();
      if (!asset || (asset.kind !== 'background_image' && asset.kind !== 'background_video')) {
        return null;
      }
      const { data } = await supabase.storage
        .from('youtube-assets')
        .createSignedUrl(asset.bucket_path, 3600);
      if (!data?.signedUrl) return null;
      return { url: data.signedUrl, isVideo: asset.kind === 'background_video' };
    },
  });
}

/** Compte les questions jouées (steps V1 / blocks V2) du meilleur attempt rejoué. */
export function useExerciseQuestionCount(exerciseId: string) {
  return useQuery({
    queryKey: ['exercise-question-count', exerciseId],
    enabled: !!exerciseId,
    queryFn: async (): Promise<number> => {
      // Nombre de blocs statement_question (table absente des types générés
      // → accès via ytDb non-typé, cf. note en tête de fichier).
      const { count: v2Count } = await ytDb
        .from('exercise_blocks')
        .select('id', { count: 'exact', head: true })
        .eq('exercise_id', exerciseId)
        .eq('block_type', 'statement_question');
      return v2Count ?? 0;
    },
  });
}

/** Somme des questions jouées sur une LISTE d'exercices (playlist). */
export function useExercisesQuestionCount(exerciseIds: string[]) {
  const key = exerciseIds.join(',');
  return useQuery({
    queryKey: ['exercises-question-count', key],
    enabled: exerciseIds.length > 0,
    queryFn: async (): Promise<number> => {
      const counts = await Promise.all(
        exerciseIds.map(async (exerciseId) => {
          const { count: v2Count } = await ytDb
            .from('exercise_blocks')
            .select('id', { count: 'exact', head: true })
            .eq('exercise_id', exerciseId)
            .eq('block_type', 'statement_question');
          return v2Count ?? 0;
        }),
      );
      return counts.reduce((a, b) => a + b, 0);
    },
  });
}

/**
 * Attempts `completed` d'exercices publiés — candidats pour une vidéo. On
 * sélectionne un ATTEMPT (pas un exercice) : le showcase rejoue un attempt
 * enregistré, donc on garantit toujours un replay valide. Inclut le contexte
 * chapitre (niveau/matière/titre) pour pré-remplir overlay + description.
 * Réutilise le pattern (et la RLS admin) de `useShowcaseCandidates`.
 */
export type VideoAttemptCandidate = {
  attempt_id: string;
  exercise_id: string;
  exercise_title: string;
  student_name: string;
  completed_at: string | null;
  content_version: 'v1' | 'v2';
  subject: SubjectSlug | null;
  level: SchoolLevel | null;
  chapter_title: string | null;
};

export function useVideoAttemptCandidates() {
  return useQuery({
    queryKey: ['video-attempt-candidates'],
    queryFn: async (): Promise<VideoAttemptCandidate[]> => {
      const { data, error } = await supabase
        .from('attempts')
        .select(
          `
          id, completed_at, exercise_id,
          profiles!attempts_student_id_fkey ( display_name:visible_name ),
          exercises!inner ( title, content_version, status, chapters ( title, subject, level ) )
        `,
        )
        .eq('status', 'completed')
        .eq('exercises.status', 'published')
        .order('completed_at', { ascending: false })
        .limit(200);
      if (error) throw new Error(error.message);
      return (data ?? []).map((a: any) => ({
        attempt_id: a.id,
        exercise_id: a.exercise_id,
        exercise_title: a.exercises?.title ?? '—',
        student_name: a.profiles?.display_name ?? 'Élève',
        completed_at: a.completed_at,
        content_version: (a.exercises?.content_version ?? 'v1') as 'v1' | 'v2',
        subject: (a.exercises?.chapters?.subject ?? null) as SubjectSlug | null,
        level: (a.exercises?.chapters?.level ?? null) as SchoolLevel | null,
        chapter_title: a.exercises?.chapters?.title ?? null,
      }));
    },
  });
}

/** Compte connecté à YouTube (OAuth) — null si non connecté. */
export function useSocialAccount(platform: SocialPlatform = 'youtube') {
  return useQuery({
    queryKey: ['social-account', platform],
    queryFn: async (): Promise<{ channel_title: string | null } | null> => {
      const { data, error } = await ytDb
        .from('social_oauth_accounts')
        .select('channel_title')
        .eq('platform', platform)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data ?? null) as { channel_title: string | null } | null;
    },
  });
}
